import { setGameHole, setGameField } from './ggGamesData.js'
import { wolfIndexFor, shuckIndexFor } from './ggPlayWolf.js'

// Wolf per-hole panel — mirrors the app's WolfPanel/BlsPanel: 🐺 WOLF header,
// rotation chips (current filled, NEXT outlined), "[Wolf]'s pick:" with each
// other player as a direct button PLUS a separate Lone Wolf button (picking
// nobody = lone), and bet cards (Double This Hole / Press). BLS adds the Shuck.
const GREEN = '#1B4D2E'
const GOLD = '#C9A84C'
const btn = (bg, fg, bd) => ({ padding: '7px 13px', borderRadius: 999, fontSize: 13, fontWeight: 700, background: bg, color: fg, border: `1px solid ${bd}`, cursor: 'pointer' })

export default function GWolfHoleInputs({ gameId, game, hole }) {
  const players = game.players || []
  const n = players.length
  if (n === 0) return null
  const opts = game.meta?.options || {}
  const isBls = opts.variant === 'blShuck'
  const wolfIdx = wolfIndexFor(hole, n, isBls)
  const nextIdx = wolfIndexFor(hole + 1, n, isBls)
  const shuckIdx = shuckIndexFor(hole, n, isBls)
  const wolf = players[wolfIdx]
  const shuck = players[shuckIdx]
  const defaultPick = isBls ? { loneWolf: false, partnerId: null, shuckPartnerId: shuck.id } : { loneWolf: true, partnerId: null }
  const pick = game.wolf?.[hole]
  const p = pick || defaultPick
  const first = (nm) => nm.split(' ')[0]

  const write = (patch) => setGameHole(gameId, 'wolf', hole, { ...defaultPick, ...p, ...patch })
  const clear = () => setGameField(gameId, `wolf/${hole}`, null)

  const wolfBet = game.wolfBet || {}
  const doubled = Number(wolfBet.multipliers?.[hole]) === 2
  const pressed = wolfBet.overrides?.[hole] !== undefined
  const toggleDouble = () => setGameField(gameId, `wolfBet/multipliers/${hole}`, doubled ? null : 2)
  const applyPress = () => {
    const v = Number(prompt('Press — new $/hole from this hole forward:', String(opts.baseBet || 2)))
    if (!Number.isFinite(v) || v <= 0) return
    for (let h = hole; h <= 18; h++) setGameField(gameId, `wolfBet/overrides/${h}`, v)
  }
  const clearPress = () => { for (let h = hole; h <= 18; h++) setGameField(gameId, `wolfBet/overrides/${h}`, null) }

  const others = players.filter((_, i) => i !== wolfIdx && (!isBls || i !== shuckIdx))
  const shuckOn = p.shuckPartnerId != null

  return (
    <div style={{ border: `2px solid ${GREEN}`, borderRadius: 14, padding: 12, marginBottom: 12, background: '#F7FBF8' }}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <span style={{ background: GREEN, color: '#fff', fontWeight: 800, fontSize: 12, padding: '3px 8px', borderRadius: 6 }}>🐺 WOLF</span>
        <span className="font-extrabold text-lg">{wolf.name}</span>
        {isBls && <span className="text-xs text-gray-500">· Shuck: <b>{shuck.name}</b></span>}
      </div>

      {/* Rotation chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {players.map((pl, idx) => {
          const cur = idx === wolfIdx
          const nxt = idx === nextIdx && !cur
          return (
            <span key={idx} style={{ position: 'relative', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
              background: cur ? GOLD : '#F5F5F5', color: cur ? '#0D2411' : '#555',
              border: nxt ? `1.5px dashed ${GOLD}` : '1px solid #E0E0E0' }}>
              {first(pl.name)}{nxt && <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, color: GOLD }}>NEXT</span>}
            </span>
          )
        })}
      </div>

      {/* Pick */}
      {pick ? (
        <div className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: '#EDF4EE' }}>
          <span className="text-sm font-semibold">
            {p.loneWolf && !p.partnerId && !p.shuckPartnerId ? '🐺 Lone Wolf — going it alone'
              : `🤝 ${[p.partnerId, isBls && shuckOn ? shuck.id : null].filter(Boolean).map((id) => players.find((x) => x.id === id)?.name).join(' + ') || '—'}`}
          </span>
          <button style={btn('#fff', GREEN, GREEN)} onClick={clear}>Change</button>
        </div>
      ) : (
        <div>
          <div className="text-sm font-semibold mb-1">{first(wolf.name)}'s pick:</div>
          <div className="flex flex-wrap gap-2">
            {others.map((o) => <button key={o.id} style={btn('#F5FAF6', '#386641', '#DDE5E1')} onClick={() => write({ loneWolf: false, partnerId: o.id })}>{first(o.name)}</button>)}
            <button style={btn(GREEN, '#fff', GREEN)} onClick={() => write({ loneWolf: true, partnerId: null, shuckPartnerId: null })}>🐺 Lone Wolf</button>
          </div>
          {isBls && (
            <div className="flex items-center gap-2 mt-2">
              <button style={btn(shuckOn ? GREEN : '#F5FAF6', shuckOn ? '#fff' : '#386641', shuckOn ? GREEN : '#DDE5E1')}
                onClick={() => write({ loneWolf: false, shuckPartnerId: shuckOn ? null : shuck.id })}>
                {shuckOn ? `✓ Shuck ${first(shuck.name)} (on team)` : `❌ Shucked ${first(shuck.name)}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Blind / Howl */}
      <div className="flex flex-wrap items-center gap-4 text-xs mt-3">
        {opts.allowHowl && <label className="flex items-center gap-1 font-semibold text-gray-600"><input type="checkbox" checked={!!p.howl} onChange={(e) => write({ howl: e.target.checked })} /> 🔊 Howl (2×)</label>}
        {opts.allowBlind && p.loneWolf && <label className="flex items-center gap-1 font-semibold text-gray-600"><input type="checkbox" checked={!!p.blind} onChange={(e) => write({ blind: e.target.checked })} /> 🙈 Blind Wolf (2×)</label>}
      </div>

      {/* Bet cards */}
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={toggleDouble} className="text-left rounded-lg px-3 py-2" style={{ border: `1.5px solid ${doubled ? GREEN : '#E0E0E0'}`, background: doubled ? '#E8F5E9' : '#fff', minWidth: 150 }}>
          <div className="text-sm font-bold">{doubled ? '✓ Doubled' : '× Double This Hole'}</div>
          <div className="text-[11px] text-gray-500">just this hole</div>
        </button>
        <button onClick={pressed ? clearPress : applyPress} className="text-left rounded-lg px-3 py-2" style={{ border: `1.5px solid ${pressed ? '#B42318' : '#E0E0E0'}`, background: pressed ? '#FEF2F2' : '#fff', minWidth: 150 }}>
          <div className="text-sm font-bold">💰 {pressed ? `Clear Press ($${wolfBet.overrides[hole]})` : 'Press / Adjust Bet'}</div>
          <div className="text-[11px] text-gray-500">from this hole forward</div>
        </button>
      </div>
    </div>
  )
}
